<template>
  <AuthCard show-intro>
    <form @submit.prevent="onSubmit">
      <div v-if="formAlertMessages.length" class="c-login__error">
        <Alert
          v-for="(msg, i) in formAlertMessages"
          :key="i"
          :type="msg.type"
          @close="formAlertMessages.splice(i, 1)"
        >
          {{ msg.message }}
        </Alert>
      </div>

      <div>
        <div class="c-login__input">
          <TextInput
            name="login"
            label="Email"
            input-type="email"
            autocomplete="email webauthn"
            placeholder="Email"
            hide-label
            required
            autofocus
            :model-value="form.email"
            :errors="getFieldErrors('email')"
            data-test="signin-email"
            @update:model-value="form.email = $event"
          />
        </div>
        <div class="c-login__input mt-2">
          <TextInput
            name="password"
            label="Password"
            is-password
            autocomplete="current-password"
            placeholder="Password"
            hide-label
            required
            :model-value="form.password"
            :errors="getFieldErrors('password')"
            data-test="signin-password"
            @update:model-value="form.password = $event"
          />
        </div>
      </div>
      <div class="d-flex justify-content-end mt-2 mb-3">
        <router-link :to="{name: 'password_reset'}">
          <small>Forgot password?</small>
        </router-link>
      </div>
      <button
        class="w-100 btn btn-lg btn-primary mb-2 rounded-1"
        type="submit"
        :disabled="submitting"
        data-test="signin-submit"
      >
        {{ submitting ? 'Signing in…' : 'Sign In' }}
      </button>
    </form>

    <template v-if="passkeySupported">
      <div class="c-login__divider">
        or
      </div>
      <button
        class="w-100 btn btn-lg btn-outline-primary rounded-1"
        type="button"
        :disabled="passkeyLoading"
        data-test="signin-passkey"
        @click="passkeyLogin"
      >
        {{ passkeyLoading ? 'Waiting for passkey…' : 'Sign in with a passkey' }}
      </button>
    </template>
  </AuthCard>
</template>

<script setup>
import {inject, onBeforeUnmount, onMounted, reactive, ref, watch} from 'vue';
import * as Sentry from '@sentry/browser';
import AuthCard from './components/AuthCard.vue';
import Alert from '@canopyllc/roots/components/Alert.vue';
import TextInput from '@canopyllc/roots/components/forms/TextInput.vue';
import {lookupSsoDomain, redirectToSamlLogin} from '../../utils/ssoRedirect.js';
import {isWebAuthnSupported} from '@/js/utils/webauthn.js';
import {usePasskeyLogin} from '@/js/composables/usePasskeyLogin.js';
import {useErrorManager} from '@canopyllc/roots/composables/errorManager.js';

const api = inject('api'),
      urls = inject('urls'),
      emailDomains = inject('emailDomains'),

      form = reactive({email: '', password: ''}),
      submitting = ref(false),
      passkeySupported = ref(false),

      {
        formAlertMessages,
        getFieldErrors,
        clearFormErrors,
        setNonFieldErrors,
        setAllauthErrors,
        handleAllauthErrors,
      } = useErrorManager(),

  // passkeyLogin powers the explicit button; startPasskeyAutofill kicks off the
  // conditional-UI (autofill) flow on mount. cancelPasskey aborts a pending
  // conditional request when the component unmounts.
      {
        loading: passkeyLoading,
        passkeyLogin,
        startConditional: startPasskeyAutofill,
        cancel: cancelPasskey,
      } = usePasskeyLogin({
        api,
        onSuccess: () => { window.location.href = urls.nextUrl || '/'; },
        onError: (next) => setAllauthErrors(next),
        onStart: () => clearFormErrors(),
      });

onMounted(() => {
  passkeySupported.value = isWebAuthnSupported();
  startPasskeyAutofill();
});

onBeforeUnmount(() => {
  cancelPasskey();
});

async function checkDomain() {
  const redirectUrl = lookupSsoDomain(form.email, emailDomains);
  if (redirectUrl === null) return;
  submitting.value = true;
  try {
    await redirectToSamlLogin(redirectUrl);
  } catch (err) {
    Sentry.captureException(err);
    setNonFieldErrors([{message: 'We could not redirect you to your single sign-on provider. Please try again.', type: 'danger'}]);
    submitting.value = false;
  }
}

watch(() => form.email, () => {
  checkDomain();
});

// Returns true when a pending allauth flow was handled (navigation away or an
// error was set), so the caller should stop. The only flow we route into is the
// MFA challenge; anything else is unexpected (email verification is off).
function handleAllauthFlows(flows) {
  if (!Array.isArray(flows) || flows.length === 0) return false;
  if (flows.some((flow) => flow.id === 'mfa_authenticate')) {
    // Full-page navigation, not a client-side router.push: the password step
    // rotates Django's CSRF cookie, and because CSRF_COOKIE_HTTPONLY is set the
    // SPA can't read the new token from the cookie — only a server render embeds
    // a fresh {{ csrf_token }}. A client-side route would keep the stale token
    // from the original page render and the 2FA POST would fail CSRF. Preserve
    // the ?next= query so the post-login redirect target survives.
    window.location.assign(`/accounts/2fa/${window.location.search}`);
    return true;
  }
  Sentry.captureMessage('Unexpected allauth flow on signin', {extra: {flows}});
  setNonFieldErrors([{message: 'Additional verification is required. Please contact support.', type: 'danger'}]);
  submitting.value = false;
  return true;
}

async function onSubmit() {
  clearFormErrors();

  const redirectUrl = lookupSsoDomain(form.email, emailDomains);
  if (redirectUrl !== null) {
    await checkDomain();
    return;
  }

  submitting.value = true;
  try {
    const response = await api.post(urls.signinApiUrl, {
      email: form.email,
      password: form.password,
    });
    if (handleAllauthFlows(response?.data?.data?.flows)) return;
    window.location.href = urls.nextUrl || '/';
  } catch (err) {
    const status = err?.response?.status;
    if (status === 409) {
      window.location.href = urls.nextUrl || '/';
      return;
    }
    // A pending MFA factor comes back as a 401 with an `mfa_authenticate` flow.
    if (status === 401 && handleAllauthFlows(err?.response?.data?.data?.flows)) return;
    if (!handleAllauthErrors(err)) {
      Sentry.captureException(err);
      setNonFieldErrors([{message: 'Something went wrong. Please try again.', type: 'danger'}]);
    }
    submitting.value = false;
  }
}
</script>
